/**
 * @component Header
 * @description 회사 홈페이지 전역 네비게이션(데스크톱 드롭다운/모바일 아코디언)을 제공하는 헤더 컴포넌트입니다.
 * @usage MainLayout 상단에 고정 배치되어 모든 페이지 이동 진입점을 제공합니다.
 */

import { navItems } from '../../constants/navigation';
import { useHeaderState } from '../../hooks/useHeaderState';

export default function Header({ pathname, onNavigate }) {
  // [상태 조회]: 헤더 활성 메뉴, 스크롤/모바일 열림 상태를 헤더 전용 훅에서 가져옵니다.
  const {
    activeSubLevel,
    activeTopLevel,
    headerClassName,
    isSolid,
    mobileOpen,
    openMenu,
    setIsHovered,
    setMobileOpen,
    setOpenMenu,
  } = useHeaderState(pathname);

  // [비즈니스] 헤더가 투명 상태일 때는 흰색 로고, 불투명 상태일 때는 컬러 로고를 표시합니다.
  const logoSrc = isSolid ? '/saehim_logo.png' : '/saehim_logo_white.png';

  return (
    <header
      className={headerClassName}
      // [이벤트 처리]: 헤더 호버 시 배경을 고정 스타일로 전환합니다.
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="container header-inner">
        <button type="button" className="brand" onClick={() => onNavigate('/')}>
          <img className="brand-logo" src={logoSrc} alt="새힘정보기술 로고" />
        </button>

        <button
          type="button"
          className={`mobile-nav-toggle ${mobileOpen ? 'is-open' : ''}`}
          // [이벤트 처리]: 모바일 햄버거 버튼 클릭 시 메뉴 패널 열림 상태를 토글합니다.
          onClick={() => setMobileOpen((current) => !current)}
          aria-label="모바일 메뉴 열기"
          aria-expanded={mobileOpen}
        >
          <span />
          <span />
          <span />
        </button>

        <nav className={`gnb ${mobileOpen ? 'is-open' : ''}`} aria-label="주요 메뉴">
          {navItems.map((item) => {
            const hasChildren = Array.isArray(item.children);
            const isOpen = openMenu === item.id;

            const handleTopLevelClick = () => {
              // [조건 분기]: 모바일 + 하위 메뉴가 있는 경우에는 페이지 이동 대신 서브메뉴를 열고 닫습니다.
              if (hasChildren && mobileOpen) {
                setOpenMenu((current) => (current === item.id ? '' : item.id));
                return;
              }

              // [이벤트 처리]: 단일 메뉴 또는 데스크톱 클릭 시 해당 경로로 이동합니다.
              onNavigate(item.href);
            };

            return (
              <div
                key={item.id}
                className={`gnb-item ${hasChildren ? 'gnb-submenu' : ''} ${isOpen ? 'is-open' : ''}`}
                onMouseEnter={() => hasChildren && !mobileOpen && setOpenMenu(item.id)}
                onMouseLeave={() => hasChildren && !mobileOpen && setOpenMenu('')}
              >
                <button
                  type="button"
                  className={`gnb-link ${hasChildren ? 'has-children' : ''} ${activeTopLevel === item.id ? 'is-active' : ''}`}
                  onClick={handleTopLevelClick}
                  aria-haspopup={hasChildren ? 'menu' : undefined}
                  aria-expanded={hasChildren ? isOpen : undefined}
                >
                  {item.label}
                  {hasChildren ? <span className={`gnb-caret ${isOpen ? 'is-open' : ''}`} aria-hidden="true" /> : null}
                </button>

                {hasChildren ? (
                  <>
                    <div className="submenu-bridge" aria-hidden="true" />
                    <div className="submenu-panel">
                      {item.children.map((child) => (
                        <button
                          key={child.id}
                          type="button"
                          className={`submenu-link ${activeSubLevel === child.id ? 'is-active' : ''}`}
                          onClick={() => onNavigate(child.href)}
                        >
                          {child.label}
                        </button>
                      ))}
                    </div>

                    <div className="mobile-submenu">
                      {item.children.map((child) => (
                        <button
                          key={`${item.id}-${child.id}`}
                          type="button"
                          className={`mobile-submenu-link ${activeSubLevel === child.id ? 'is-active' : ''}`}
                          onClick={() => onNavigate(child.href)}
                        >
                          {child.label}
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            );
          })}
        </nav>
      </div>
    </header>
  );
}
